import type {
  CaseEditableDto,
  CaseEditableOrgOptionDto,
  CaseEditableSelectOptionDto,
} from '../dto/case.dto';
import { genderTextToCode } from '@/shared/enums/gender';

type RawRecord = Record<string, unknown>;

function isRecord(value: unknown): value is RawRecord {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown): string {
  return String(value ?? '').trim();
}

function asNumber(value: unknown): number | null {
  const n = Number(value ?? 0);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function asBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const normalized = asString(value).toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === '是';
}

function genderNumber(value: unknown): number {
  return genderTextToCode(value);
}

function pickName(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (!isRecord(value)) return '';
  for (const key of ['fullname', 'name', 'title', 'label', 'show_name']) {
    const field = value[key];
    if (typeof field === 'string' && field.trim()) return field.trim();
    if (typeof field === 'number') return String(field);
  }
  return '';
}

function firstText(values: unknown[]): string {
  for (const value of values) {
    const text = pickName(value);
    if (text) return text;
  }
  return '';
}

function relationId(row: RawRecord, relationKey: string, rawKey: string): number | null {
  const relation = row[relationKey];
  if (isRecord(relation)) {
    const id = asNumber(relation.id);
    if (id != null) return id;
  }
  return asNumber(row[rawKey]);
}

function extractArchive(row: RawRecord): RawRecord {
  const raw =
    row.extend ??
    row.case_maindata_archive ??
    row.caseMaindataArchive ??
    row.archive ??
    row.case_archive ??
    row.caseMaindataArchiveRecord;
  const archive = Array.isArray(raw) ? raw[0] : raw;
  return isRecord(archive) ? archive : {};
}

function option(label: string, value: number | null): CaseEditableSelectOptionDto | null {
  if (!value) return null;
  return { label: label || `#${value}`, value };
}

function orgOptions(params: {
  hospitalId: number | null;
  departmentId: number | null;
  departmentCategoryId: number | null;
  doctorId: number | null;
  siteId: number | null;
  hospital: string;
  department: string;
  doctor: string;
  site: string;
}): CaseEditableOrgOptionDto[] {
  const hospital = option(params.hospital, params.hospitalId);
  if (!hospital) return [];

  const hospitalOption: CaseEditableOrgOptionDto = {
    ...hospital,
    isLeaf: false,
    meta: {
      siteId: params.siteId,
      siteText: params.site,
    },
    children: [],
  };

  const department = option(params.department, params.departmentId);
  if (!department) return [hospitalOption];

  const departmentOption: CaseEditableOrgOptionDto = {
    ...department,
    isLeaf: false,
    meta: {
      departmentCategoryId: params.departmentCategoryId,
    },
    children: [],
  };
  hospitalOption.children = [departmentOption];

  const doctor = option(params.doctor, params.doctorId);
  if (doctor) {
    departmentOption.children = [{ ...doctor, isLeaf: true }];
  }

  return [hospitalOption];
}

function regionCodes(archive: RawRecord): string[] {
  return [
    asString(archive.province_id),
    asString(archive.city_id),
    asString(archive.county_id),
    asString(archive.town_id),
  ].filter(Boolean);
}

export class CaseEditMapper {
  static toEditable(row: unknown): CaseEditableDto | null {
    if (!isRecord(row)) return null;
    const caseId = asNumber(row.id);
    if (!caseId) return null;

    const archive = extractArchive(row);
    const hospitalId = relationId(row, 'hospital', 'ga_hospital_id');
    const departmentId = relationId(row, 'hospital_department', 'ga_hospital_department_id');
    const departmentCategoryId = relationId(
      row,
      'hospital_department_category',
      'ga_hospital_department_category_id',
    );
    const doctorId = relationId(row, 'doctor', 'ga_doctor_id');
    const siteId = relationId(row, 'site', 'ga_site_id');
    const bodyPartId = relationId(archive, 'bodypart', 'bodypart_id');
    const indicationId = relationId(archive, 'indication', 'indication_id');
    const treatmentPlanId = relationId(archive, 'treatment_plan', 'treatment_plan_id');

    const hospital = firstText([row.hospital]);
    const department = firstText([row.hospital_department]);
    const doctor = firstText([row.doctor]);
    const site = firstText([row.site]);
    const bodyPart = firstText([archive.bodypart, archive.bodyPart, archive.medical_advice_bodypart]);
    const indication = firstText([archive.indication, archive.ga_indication]);
    const treatmentPlan = firstText([archive.treatment_plan, archive.ga_treatment_plan]);

    return {
      caseId,
      name: asString(row.name),
      hospitalId,
      departmentId,
      departmentCategoryId,
      doctorId,
      siteId,
      factorId: null,
      patientName: firstText([archive.name]),
      gender: genderNumber(archive.gender),
      age: asString(archive.age),
      idNumber: asString(archive.id_number),
      bloodType: asString(archive.blood_type),
      visitNo: asString(archive.visit_no),
      hospitalizationNumber: asString(archive.hospitalization_number),
      admissionTime: asString(archive.admission_time),
      admissionDiagnosis: asString(archive.admission_diagnosis),
      isHospitalized: asBoolean(archive.is_hospitalized),
      bodyPartId,
      indicationId,
      treatmentPlanId,
      regionCodes: regionCodes(archive),
      provinceId: asString(archive.province_id),
      province: asString(archive.province),
      cityId: asString(archive.city_id),
      city: asString(archive.city),
      countyId: asString(archive.county_id),
      county: asString(archive.county),
      townId: asString(archive.town_id),
      town: asString(archive.town),
      remark: asString(archive.remark),
      orgPickedText: { hospital, department, doctor, site },
      orgOptions: orgOptions({
        hospitalId,
        departmentId,
        departmentCategoryId,
        doctorId,
        siteId,
        hospital,
        department,
        doctor,
        site,
      }),
      centerOption: option(site, siteId),
      bodyPartOption: option(bodyPart, bodyPartId),
      indicationOption: option(indication, indicationId),
      treatmentPlanOption: option(treatmentPlan, treatmentPlanId),
    };
  }
}
